import { useState, useEffect } from 'react';
import { useSEO, generateOrganizationSchema, generateWebSiteSchema, generateServiceSchema } from '../../utils/seo';

export default function HomePage() {
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // SEO Configuration
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://runxing.cn';
  
  useSEO({
    title: '润星集团官网 | 深度服务品牌全链路增长 - 短视频、直播与艺人经纪',
    description: '润星集团是立足内容营销领域，以明星、艺人及网红主播为核心驱动力的综合性营销服务集团。年营收超500亿，服务1000+品牌，业务覆盖电商直播、短视频切片、短剧、自有品牌、明星经纪、互动营销、数字供应链、产业链投资等多维业务单元。',
    keywords: '润星集团,润星,直播,短视频,电商,主播,短剧,润星娱乐,快手直播,抖音直播,润心娱乐,内容电商,短视频带货,TikTok直播',
    canonical: `${siteUrl}/`,
    ogImage: 'https://www.mizean.com/zb_users/upload/2025/12/Rstar%E6%B6%A6%E6%98%9F%E9%9B%86%E5%9B%A2logo.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(),
        generateWebSiteSchema(),
        generateServiceSchema(),
      ],
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      const sections = ['home', 'about', 'services', 'partners', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowSuccessToast(false);
    setShowErrorToast(false);
    
    try {
      const response = await fetch('https://formsubmit.co/ajax/info@we3.vip', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          _subject: '【润星娱乐】新合作咨询通知',
          message: `新客户通过官网提交了咨询，请尽快联系。客户邮箱：${email}`
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setShowSuccessToast(true);
        setEmail('');
        setTimeout(() => setShowSuccessToast(false), 5000);
      } else {
        setShowErrorToast(true);
        setTimeout(() => setShowErrorToast(false), 5000);
      }
    } catch (error) {
      console.error('Submission failed:', error);
      setShowErrorToast(true);
      setTimeout(() => setShowErrorToast(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const brands = {
    row1: ['HUAWEI 华为', 'XIAOMI 小米', 'MIDEA 美的', 'HAIER 海尔', 'DJI 大疆', 'GREE 格力', 'TCL', 'HISENSE 海信', 'OPPO', 'VIVO', 'ROBOROCK 石头', 'ECOVACS 科沃斯', 'HONOR 荣耀', 'LENOVO 联想'],
    row2: ['PROYA 珀莱雅', 'FLORASIS 花西子', 'WINONA 薇诺娜', 'CHANDO 自然堂', 'MAOGEPING 毛戈平', 'PERFECT DIARY', 'COLORKEY', 'DR.YU 玉泽', 'LANCOME 兰蔻', "L'OREAL 欧莱雅", 'ESTEE LAUDER', 'SK-II', 'SHISEIDO 资生堂', 'CPB'],
    row3: ['ANTA 安踏', 'LI-NING 李宁', 'XTEP 特步', 'FILA', 'BOSIDENG 波司登', 'PEACEBIRD 太平鸟', 'URBAN REVIVO', 'SEMIR 森马', 'HLA 海澜之家', 'MO&Co.', 'NIKE', 'ADIDAS', 'LULULEMON', 'UNIQLO'],
    row4: ['认养一头牛', 'YILI 伊利', 'MENGNIU 蒙牛', 'WANT WANT 旺旺', 'GENKI 元气森林', '康师傅', 'THREE SQUIRRELS', 'BE&CHEERY 百草味', 'TSINGTAO 青岛', 'SNOW 雪花', 'COCA COLA', 'PEPSI 百事', 'STARBUCKS', 'KFC'],
    row5: ['BLUEMOON 蓝月亮', 'LIBY 立白', 'VINDA 维达', 'BREEZE 清风', 'C&S 洁柔', 'YUNNAN BAIYAO', 'ADOLPH 阿道夫', 'SAKY 舒客', 'DARMAL', 'SAFEGUARD 舒肤佳', 'P&G 宝洁', 'UNILEVER 联合利华', 'WALCH 威露士', 'DETTOL 滴露']
  };

  return (
    <div className="bg-gray-950 text-white font-sans antialiased selection:bg-purple-600 selection:text-white relative">
      {/* Success Toast */}
      {showSuccessToast && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowSuccessToast(false)}
          ></div>
          <div className="relative bg-green-500 text-white px-8 py-6 rounded-2xl shadow-2xl flex items-center space-x-4 min-w-[360px] max-w-md animate-fade-in-up">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-check-line text-3xl"></i>
            </div>
            <div className="flex-1">
              <p className="font-bold text-lg">提交成功！</p>
              <p className="text-sm text-green-100 mt-1">我们的团队将在24小时内与您联系</p>
            </div>
            <button 
              onClick={() => setShowSuccessToast(false)}
              className="text-white/80 hover:text-white transition cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {showErrorToast && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowErrorToast(false)}
          ></div>
          <div className="relative bg-red-500 text-white px-8 py-6 rounded-2xl shadow-2xl flex items-center space-x-4 min-w-[360px] max-w-md animate-fade-in-up">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <div className="flex-1">
              <p className="font-bold text-lg">提交失败</p>
              <p className="text-sm text-red-100 mt-1">请稍后重试或发送邮件至 info@we3.vip</p>
            </div>
            <button 
              onClick={() => setShowErrorToast(false)}
              className="text-white/80 hover:text-white transition cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className={`fixed w-full z-40 transition-all duration-300 ${isScrolled ? 'bg-gray-950/95 backdrop-blur-lg border-b border-gray-800 shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center gap-3 cursor-pointer group" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <div className="w-10 h-10 flex items-center justify-center">
                  <img 
                    src="https://www.mizean.com/zb_users/upload/2025/12/Rstar%E6%B6%A6%E6%98%9F%E9%9B%86%E5%9B%A2logo.jpg" 
                    alt="润星 Logo" 
                    className="w-full h-full object-contain drop-shadow-lg group-hover:scale-110 transition-transform"
                  />
                </div>
                <span 
                  className="text-2xl font-black tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-purple-600 group-hover:scale-105 transition-transform inline-block"
                >
                  RUNXING · 润星
                </span>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <button 
                  onClick={() => scrollToSection('home')}
                  className={`hover:text-yellow-400 transition px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer ${activeSection === 'home' ? 'text-yellow-400' : ''}`}
                >
                  首页
                </button>
                <button 
                  onClick={() => scrollToSection('about')}
                  className={`hover:text-yellow-400 transition px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer ${activeSection === 'about' ? 'text-yellow-400' : ''}`}
                >
                  关于我们
                </button>
                <button 
                  onClick={() => scrollToSection('services')}
                  className={`hover:text-yellow-400 transition px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer ${activeSection === 'services' ? 'text-yellow-400' : ''}`}
                >
                  核心业务
                </button>
                <button 
                  onClick={() => scrollToSection('partners')}
                  className={`hover:text-yellow-400 transition px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer ${activeSection === 'partners' ? 'text-yellow-400' : ''}`}
                >
                  合作品牌
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-full text-sm font-bold transition shadow-lg shadow-purple-500/30 whitespace-nowrap cursor-pointer"
                >
                  联系合作
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 focus:outline-none transition cursor-pointer"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-gray-950/98 backdrop-blur-lg border-t border-gray-800">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <button
                onClick={() => scrollToSection('home')}
                className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-gray-800 hover:text-yellow-400 transition cursor-pointer ${activeSection === 'home' ? 'text-yellow-400 bg-gray-800' : 'text-gray-300'}`}
              >
                首页
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-gray-800 hover:text-yellow-400 transition cursor-pointer ${activeSection === 'about' ? 'text-yellow-400 bg-gray-800' : 'text-gray-300'}`}
              >
                关于我们
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-gray-800 hover:text-yellow-400 transition cursor-pointer ${activeSection === 'services' ? 'text-yellow-400 bg-gray-800' : 'text-gray-300'}`}
              >
                核心业务
              </button>
              <button
                onClick={() => scrollToSection('partners')}
                className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-gray-800 hover:text-yellow-400 transition cursor-pointer ${activeSection === 'partners' ? 'text-yellow-400 bg-gray-800' : 'text-gray-300'}`}
              >
                合作品牌
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-bold bg-purple-600 hover:bg-purple-700 text-white transition cursor-pointer mt-2"
              >
                联系合作
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <div id="home" className="relative bg-gray-950 overflow-hidden min-h-screen flex items-center justify-center pt-20">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
          <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-[128px] opacity-50 animate-pulse"></div>
          <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-30"></div>
          <img 
            src="https://readdy.ai/api/search-image?query=Minimalist%20technology%20background%20with%20clean%20geometric%20lines%2C%20subtle%20grid%20patterns%2C%20soft%20gradient%20from%20deep%20blue%20to%20purple%2C%20floating%20abstract%20geometric%20shapes%2C%20simple%20circuit%20board%20patterns%2C%20modern%20tech%20aesthetic%2C%20clean%20white%20accent%20lines%2C%20minimal%20particle%20effects%2C%20professional%20corporate%20tech%20style%2C%20depth%20and%20layers%2C%20soft%20ambient%20lighting%2C%20ultra-wide%20panoramic%20view%2C%20high-end%20business%20technology%20visualization%2C%20sleek%20and%20sophisticated%20design&width=1920&height=1080&seq=hero-minimal-tech&orientation=landscape" 
            alt="Minimalist Technology Background" 
            className="absolute w-full h-full object-cover opacity-20 mix-blend-overlay"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-7xl font-black tracking-tight mb-6 animate-fade-in-up">
            以内容驱动<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-purple-600">
              重塑品牌全链路增长
            </span>
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-300 animate-fade-in-up animation-delay-200">
            年营收超 500 亿的全球化多元营销集团
          </p>
          <div className="mt-10 flex justify-center gap-4 animate-fade-in-up animation-delay-400">
            <button 
              onClick={() => scrollToSection('services')}
              className="px-8 py-4 border border-transparent text-base font-bold rounded-full text-white bg-purple-600 hover:bg-purple-700 md:text-lg shadow-lg shadow-purple-500/40 transition transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              探索业务
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className="px-8 py-4 border border-gray-600 text-base font-bold rounded-full text-gray-300 hover:bg-gray-800 md:text-lg transition whitespace-nowrap cursor-pointer"
            >
              了解润星
            </button>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-gray-900 border-y border-gray-800 relative z-20">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 text-center">
            <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-2xl border border-gray-700 hover:border-gray-600 transition">
              <dt className="text-sm font-medium text-gray-400 truncate">旗下艺人</dt>
              <dd className="mt-1 text-4xl font-black text-white">30 +</dd>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-2xl border border-gray-700 hover:border-gray-600 transition">
              <dt className="text-sm font-medium text-gray-400 truncate">年营业额</dt>
              <dd className="mt-1 text-4xl font-black text-yellow-400">500亿 +</dd>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-2xl border border-gray-700 hover:border-gray-600 transition">
              <dt className="text-sm font-medium text-gray-400 truncate">合作品牌</dt>
              <dd className="mt-1 text-4xl font-black text-white">1000 +</dd>
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div id="about" className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div className="mb-12 lg:mb-0">
              <h2 className="text-yellow-400 font-semibold tracking-wide uppercase text-sm">About Us</h2>
              <h3 className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-white sm:text-4xl">
                全球化娱乐营销引擎
              </h3>
              <p className="mt-4 text-lg text-gray-400 leading-relaxed">
                立足内容，以明星、艺人及网红主播为核心驱动力的综合性营销企业。
              </p>
              <p className="mt-4 text-lg text-gray-400 leading-relaxed">
                我们深耕互联网内容营销赛道，不只是制造流量，更致力于将流量转化为品牌资产。通过深度整合"人、货、场"，我们为品牌提供从声量爆发到销量转化的全链路增长服务。
              </p>
              <ul className="mt-8 space-y-4 text-gray-300">
                <li className="flex items-center">
                  <i className="ri-check-line text-yellow-400 text-xl mr-3"></i>
                  深耕内容营销赛道
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-yellow-400 text-xl mr-3"></i>
                  全链路数字化赋能
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-yellow-400 text-xl mr-3"></i>
                  超头部明星艺人资源库
                </li>
              </ul>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-purple-900/20 border border-gray-800 group">
              <img 
                className="w-full object-cover h-[500px] group-hover:scale-105 transition-transform duration-500" 
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=1974&auto=format&fit=crop" 
                alt="Office Meeting"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
              <div className="absolute bottom-0 p-6">
                <p className="text-white font-bold text-xl">赋能品牌 · 智赢未来</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div id="services" className="py-24 bg-gray-950 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-purple-400 font-semibold tracking-wide uppercase text-sm">Our Services</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-white sm:text-4xl">
              四大核心业务模块
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-400 mx-auto">
              构建从内容种草到销售转化的商业闭环
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl hover:bg-gray-800/70 transition duration-300 border border-gray-700 group cursor-pointer">
              <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition">
                <i className="ri-live-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">电商直播</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                打造现象级直播间，涵盖达人混播与品牌店播，以超高GMV转化率为核心指标。
              </p>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl hover:bg-gray-800/70 transition duration-300 border border-gray-700 group cursor-pointer">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition">
                <i className="ri-star-line text-yellow-400 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">明星经纪</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                独家签约一线艺人与潜力新星，精准匹配品牌调性，实现声量与形象的双重提升。
              </p>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl hover:bg-gray-800/70 transition duration-300 border border-gray-700 group cursor-pointer">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition">
                <i className="ri-chat-smile-3-line text-blue-400 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">互动营销</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                创意驱动的社交媒体营销，制造全网热搜话题，深度连接 Z 世代消费者。
              </p>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl hover:bg-gray-800/70 transition duration-300 border border-gray-700 group cursor-pointer">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition">
                <i className="ri-links-line text-green-400 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">数字供应链</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                AI 赋能选品与销售管理，链接全球优质货源，为直播电商提供强大的后端支持。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Partners Section */}
      <div id="partners" className="bg-black py-16 overflow-hidden border-b border-gray-800">
        <div className="text-center mb-12">
          <p className="text-yellow-400 font-semibold tracking-wide uppercase text-sm">Trusted Partners</p>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-white sm:text-4xl">
            合作品牌
          </p>
          <p className="mt-4 max-w-2xl text-xl text-gray-400 mx-auto">
            行业头部品牌的信任之选
          </p>
        </div>
        <div className="space-y-5 opacity-90">
          {/* Row 1 - Left to Right */}
          <div className="relative flex overflow-hidden">
            <div className="flex animate-scroll-left hover:[animation-play-state:paused]">
              <div className="flex">
                {brands.row1.map((brand, idx) => (
                  <div key={idx} className="mx-3 px-6 py-3 bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg text-sm font-medium text-gray-300 whitespace-nowrap hover:bg-gray-800 hover:text-white transition cursor-pointer">
                    {brand}
                  </div>
                ))}
              </div>
              <div className="flex">
                {brands.row1.map((brand, idx) => (
                  <div key={`dup-${idx}`} className="mx-3 px-6 py-3 bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg text-sm font-medium text-gray-300 whitespace-nowrap hover:bg-gray-800 hover:text-white transition cursor-pointer">
                    {brand}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Row 2 - Right to Left */}
          <div className="relative flex overflow-hidden">
            <div className="flex animate-scroll-right hover:[animation-play-state:paused]">
              <div className="flex">
                {brands.row2.map((brand, idx) => (
                  <div key={idx} className="mx-3 px-6 py-3 bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg text-sm font-medium text-gray-300 whitespace-nowrap hover:bg-gray-800 hover:text-white transition cursor-pointer">
                    {brand}
                  </div>
                ))}
              </div>
              <div className="flex">
                {brands.row2.map((brand, idx) => (
                  <div key={`dup-${idx}`} className="mx-3 px-6 py-3 bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg text-sm font-medium text-gray-300 whitespace-nowrap hover:bg-gray-800 hover:text-white transition cursor-pointer">
                    {brand}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Row 3 - Left to Right */}
          <div className="relative flex overflow-hidden">
            <div className="flex animate-scroll-left hover:[animation-play-state:paused]">
              <div className="flex">
                {brands.row3.map((brand, idx) => (
                  <div key={idx} className="mx-3 px-6 py-3 bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg text-sm font-medium text-gray-300 whitespace-nowrap hover:bg-gray-800 hover:text-white transition cursor-pointer">
                    {brand}
                  </div>
                ))}
              </div>
              <div className="flex">
                {brands.row3.map((brand, idx) => (
                  <div key={`dup-${idx}`} className="mx-3 px-6 py-3 bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg text-sm font-medium text-gray-300 whitespace-nowrap hover:bg-gray-800 hover:text-white transition cursor-pointer">
                    {brand}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Row 4 - Right to Left */}
          <div className="relative flex overflow-hidden">
            <div className="flex animate-scroll-right hover:[animation-play-state:paused]">
              <div className="flex">
                {brands.row4.map((brand, idx) => (
                  <div key={idx} className="mx-3 px-6 py-3 bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg text-sm font-medium text-gray-300 whitespace-nowrap hover:bg-gray-800 hover:text-white transition cursor-pointer">
                    {brand}
                  </div>
                ))}
              </div>
              <div className="flex">
                {brands.row4.map((brand, idx) => (
                  <div key={`dup-${idx}`} className="mx-3 px-6 py-3 bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg text-sm font-medium text-gray-300 whitespace-nowrap hover:bg-gray-800 hover:text-white transition cursor-pointer">
                    {brand}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Row 5 - Left to Right */}
          <div className="relative flex overflow-hidden">
            <div className="flex animate-scroll-left hover:[animation-play-state:paused]">
              <div className="flex">
                {brands.row5.map((brand, idx) => (
                  <div key={idx} className="mx-3 px-6 py-3 bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg text-sm font-medium text-gray-300 whitespace-nowrap hover:bg-gray-800 hover:text-white transition cursor-pointer">
                    {brand}
                  </div>
                ))}
              </div>
              <div className="flex">
                {brands.row5.map((brand, idx) => (
                  <div key={`dup-${idx}`} className="mx-3 px-6 py-3 bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg text-sm font-medium text-gray-300 whitespace-nowrap hover:bg-gray-800 hover:text-white transition cursor-pointer">
                    {brand}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact" className="bg-gradient-to-r from-purple-600 to-indigo-900 py-16 border-b border-white/10 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            准备好引爆品牌增长了吗？
          </h2>
          <p className="mt-4 text-xl text-indigo-100 mb-8">
            即刻联系我们获取定制化方案。
          </p>
          <form 
            id="consultation-form"
            data-readdy-form
            className="max-w-lg mx-auto space-y-4" 
            onSubmit={handleSubmit}
          >
            <input 
              type="email" 
              name="email" 
              placeholder="请输入您的企业邮箱" 
              required 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-5 py-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition"
            />
            <button 
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? '提交中...' : '立即咨询'}
            </button>
          </form>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-800 pb-10 pt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center">
          <div className="mb-4 md:mb-0 text-center md:text-left">
            <span className="text-2xl font-bold text-white">Runxing.cn</span>
            <p className="text-gray-500 text-sm mt-1">© 2025 润星 版权所有.</p>
          </div>
          <div className="flex space-x-6 text-gray-400 text-sm">
            <button 
              onClick={() => setShowPrivacyModal(true)}
              className="hover:text-white transition cursor-pointer"
            >
              隐私政策
            </button>
            <button 
              onClick={() => setShowTermsModal(true)}
              className="hover:text-white transition cursor-pointer"
            >
              服务条款
            </button>
            <a 
              href="https://we3.vip/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-white transition cursor-pointer"
            >
              Website Builder
            </a>
          </div>
        </div>
      </footer>

      {/* Privacy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 z-50" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowPrivacyModal(false)}
          ></div>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-gray-900 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl border border-gray-700">
                <div className="bg-gray-900 px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                      <h3 className="text-xl font-semibold leading-6 text-white mb-4" id="modal-title">
                        隐私政策
                      </h3>
                      <div className="mt-2 text-gray-300 text-sm space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                        <p><strong>生效日期：2025年12月1日</strong></p>
                        <p>润星（以下简称"我们"）非常重视您的隐私保护。本隐私政策旨在向您说明我们如何收集、使用、存储、共享和保护您的个人信息，以及您享有的相关权利。</p>
                        
                        <h4 className="font-bold text-white mt-4">一、适用范围</h4>
                        <p>本隐私政策适用于您通过润星官方网站（runxing.cn）使用我们提供的服务。本政策将帮助您了解以下内容：</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li>我们如何收集和使用您的个人信息</li>
                          <li>我们如何使用Cookie和同类技术</li>
                          <li>我们如何共享、转让、公开披露您的个人信息</li>
                          <li>我们如何保护您的个人信息</li>
                          <li>您的权利</li>
                          <li>本政策如何更新</li>
                        </ul>

                        <h4 className="font-bold text-white mt-4">二、我们如何收集和使用您的个人信息</h4>
                        <p><strong>2.1 我们收集的信息</strong></p>
                        <p>当您通过"联系合作"表单与我们联系时，我们会收集您主动提供的以下信息：</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li>企业邮箱地址</li>
                          <li>您在表单中填写的其他信息</li>
                        </ul>
                        
                        <p className="mt-2"><strong>2.2 信息使用目的</strong></p>
                        <p>我们收集和使用您的个人信息用于以下目的：</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li>回应您的咨询和业务需求</li>
                          <li>与您进行商务沟通和合作洽谈</li>
                          <li>向您提供定制化的营销服务方案</li>
                          <li>改进我们的服务质量</li>
                        </ul>

                        <h4 className="font-bold text-white mt-4">三、我们如何使用Cookie和同类技术</h4>
                        <p>本网站为静态展示网站，不使用Cookie或其他追踪技术收集您的浏览行为、设备信息或位置信息。</p>

                        <h4 className="font-bold text-white mt-4">四、我们如何共享、转让、公开披露您的个人信息</h4>
                        <p><strong>4.1 共享</strong></p>
                        <p>我们不会向任何第三方出售您的个人信息。我们仅在以下情况下共享您的信息：</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li>获得您的明确同意</li>
                          <li>根据法律法规或政府主管部门的强制性要求</li>
                          <li>与我们的关联公司共享，用于提供服务</li>
                        </ul>
                        
                        <p className="mt-2"><strong>4.2 转让</strong></p>
                        <p>我们不会将您的个人信息转让给任何公司、组织和个人，但以下情况除外：</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li>获得您的明确同意</li>
                          <li>在涉及合并、收购或破产清算时，如涉及个人信息转让，我们会要求新的持有您个人信息的公司继续受本隐私政策的约束</li>
                        </ul>
                        
                        <p className="mt-2"><strong>4.3 公开披露</strong></p>
                        <p>我们仅会在以下情况下公开披露您的个人信息：</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li>获得您的明确同意</li>
                          <li>基于法律法规、法律程序、诉讼或政府主管部门强制性要求</li>
                        </ul>

                        <h4 className="font-bold text-white mt-4">五、我们如何保护您的个人信息</h4>
                        <p>我们非常重视个人信息安全，采取符合业界标准的安全措施来保护您的个人信息：</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li>使用加密技术确保数据传输安全</li>
                          <li>建立严格的数据访问权限控制和监控机制</li>
                          <li>定期对员工进行信息安全培训</li>
                          <li>制定个人信息安全事件应急预案</li>
                        </ul>

                        <h4 className="font-bold text-white mt-4">六、您的权利</h4>
                        <p>根据《中华人民共和国个人信息保护法》，您对自己的个人信息享有以下权利：</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li><strong>知情权和决定权：</strong>您有权知晓我们处理您个人信息的情况，并有权决定是否同意我们处理您的个人信息</li>
                          <li><strong>访问权：</strong>您有权访问您的个人信息</li>
                          <li><strong>更正权：</strong>当您发现我们处理的个人信息有错误时，您有权要求我们更正</li>
                          <li><strong>删除权：</strong>在特定情况下，您有权要求我们删除您的个人信息</li>
                          <li><strong>撤回同意权：</strong>您有权撤回您此前作出的同意</li>
                        </ul>
                        <p className="mt-2">如需行使上述权利，请通过以下方式联系我们：info@we3.vip</p>

                        <h4 className="font-bold text-white mt-4">七、未成年人信息保护</h4>
                        <p>我们非常重视未成年人的个人信息保护。本网站主要面向企业用户，不针对未成年人。如果您是未成年人，请在监护人的陪同下使用我们的服务。</p>

                        <h4 className="font-bold text-white mt-4">八、本政策如何更新</h4>
                        <p>我们可能适时修订本隐私政策。当隐私政策发生变更时，我们会在网站上发布最新版本，并通过适当方式提醒您相关内容的更新。</p>

                        <h4 className="font-bold text-white mt-4">九、如何联系我们</h4>
                        <p>如果您对本隐私政策有任何疑问、意见或建议，请通过以下方式与我们联系：</p>
                        <p className="ml-4">电子邮箱：info@we3.vip</p>
                        <p className="ml-4">我们将在15个工作日内回复您的请求。</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-800 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button 
                    type="button" 
                    className="inline-flex w-full justify-center rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 sm:ml-3 sm:w-auto whitespace-nowrap cursor-pointer" 
                    onClick={() => setShowPrivacyModal(false)}
                  >
                    我已阅读
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowTermsModal(false)}
          ></div>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-gray-900 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl border border-gray-700">
                <div className="bg-gray-900 px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                      <h3 className="text-xl font-semibold leading-6 text-white mb-4">
                        服务条款
                      </h3>
                      <div className="mt-2 text-gray-300 text-sm space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                        <p><strong>生效日期：2025年12月1日</strong></p>
                        <p>欢迎访问润星官方网站（runxing.cn）。在使用本网站之前，请您仔细阅读并充分理解本服务条款。您访问或使用本网站即表示您同意接受本服务条款的全部内容。</p>

                        <h4 className="font-bold text-white mt-4">一、服务说明</h4>
                        <p>1.1 本网站由润星运营，为用户提供企业信息展示、业务介绍及在线咨询等服务。</p>
                        <p>1.2 我们保留随时修改或中断服务而不需通知用户的权利。我们不需对用户或第三方负责。</p>

                        <h4 className="font-bold text-white mt-4">二、知识产权声明</h4>
                        <p>2.1 本网站所包含的全部内容，包括但不限于文字、图片、图形、音频、视频、软件、程序、版面设计、商标、标识等（以下统称"网站内容"），均由润星或其关联公司、合作方依法拥有其知识产权，受《中华人民共和国著作权法》、《中华人民共和国商标法》及其他相关法律法规的保护。</p>
                        <p>2.2 未经润星书面许可，任何单位或个人不得以任何方式复制、传播、展示、镜像、上传、下载、修改、编辑本网站内容，或将其用于商业目的。</p>
                        <p>2.3 本网站所使用的"润星"及相关商标、标识的所有权及知识产权归润星所有，未经授权不得使用。</p>

                        <h4 className="font-bold text-white mt-4">三、用户行为规范</h4>
                        <p>在使用本网站服务时，您承诺遵守以下规定：</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li>遵守中华人民共和国相关法律法规</li>
                          <li>不得利用本网站从事任何违法或不当行为</li>
                          <li>不得干扰或破坏网站服务器的正常运行</li>
                          <li>不得传播任何病毒、木马或其他恶意程序</li>
                          <li>不得侵犯他人的合法权益</li>
                        </ul>

                        <h4 className="font-bold text-white mt-4">四、免责声明</h4>
                        <p>4.1 本网站展示的年营业额、合作品牌数量、艺人数量等数据仅供参考，具体以实际情况为准。</p>
                        <p>4.2 本网站所展示的案例、成果等信息仅作为业务能力展示，不构成任何承诺或保证。具体合作内容、服务标准、费用等以双方正式签署的书面合同为准。</p>
                        <p>4.3 我们力求本网站所提供信息的准确性和完整性，但不对信息的准确性、完整性、及时性作出任何明示或暗示的保证。</p>
                        <p>4.4 因不可抗力或其他本网站无法控制的原因导致的服务中断或其他缺陷，本网站不承担任何责任。</p>
                        <p>4.5 用户因使用本网站服务而产生的任何直接、间接、偶然、特殊及后续的损害，本网站不承担任何责任。</p>

                        <h4 className="font-bold text-white mt-4">五、第三方链接</h4>
                        <p>5.1 本网站可能包含指向第三方网站的链接。这些链接仅为方便用户而提供。</p>
                        <p>5.2 我们不对第三方网站的内容、隐私政策或做法负责，也不对因访问或使用第三方网站而产生的任何损失或损害承担责任。</p>
                        <p>5.3 您访问第三方网站的风险由您自行承担。</p>

                        <h4 className="font-bold text-white mt-4">六、信息提交</h4>
                        <p>6.1 当您通过本网站提交咨询信息时，即表示您同意我们按照《隐私政策》的规定收集、使用和处理您的个人信息。</p>
                        <p>6.2 您保证所提交的信息真实、准确、完整，不侵犯任何第三方的合法权益。</p>

                        <h4 className="font-bold text-white mt-4">七、服务变更与终止</h4>
                        <p>7.1 我们保留随时修改本服务条款的权利。修改后的服务条款一经在本网站公布即生效。</p>
                        <p>7.2 因系统维护、升级或其他原因导致的服务中断，我们将尽力减少影响，但不承担由此产生的任何责任。</p>

                        <h4 className="font-bold text-white mt-4">八、法律适用与争议解决</h4>
                        <p>8.1 本服务条款的订立、执行、解释及争议解决均适用中华人民共和国法律。</p>
                        <p>8.2 如本服务条款的任何条款被认定为无效或不可执行，该条款应在最小必要范围内被删除或修改，其余条款继续有效。</p>
                        <p>8.3 因本服务条款引起的或与本服务条款有关的任何争议，双方应首先通过友好协商解决。协商不成的，任何一方均可向润星所在地有管辖权的人民法院提起诉讼。</p>

                        <h4 className="font-bold text-white mt-4">九、条款的修改与更新</h4>
                        <p>9.1 我们保留随时修改本服务条款的权利。修改后的服务条款一经在本网站公布即生效。</p>
                        <p>9.2 如果您不同意修改后的服务条款，应立即停止使用本网站服务。您继续使用本网站服务将被视为接受修改后的服务条款。</p>

                        <h4 className="font-bold text-white mt-4">十、联系我们</h4>
                        <p>如果您对本服务条款有任何疑问或建议，请通过以下方式联系我们：</p>
                        <p className="ml-4">电子邮箱：info@we3.vip</p>
                        <p className="ml-4">我们将在15个工作日内回复您的请求。</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-800 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button 
                    type="button" 
                    className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-100 sm:ml-3 sm:w-auto whitespace-nowrap cursor-pointer" 
                    onClick={() => setShowTermsModal(false)}
                  >
                    关闭
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
